import { Plugin, ContextualBalloon, LabeledFieldView, createLabeledInputText } from 'ckeditor5';

export default class CustomTableProperties extends Plugin {
    static get pluginName() {
        return 'CustomTableProperties';
    }

    init() {
        const editor = this.editor;
        const schema = editor.model.schema;
        const conversion = editor.conversion;
        const contextualBalloonPlugin = editor.plugins.get(ContextualBalloon);
        const { t } = editor.locale;

        schema.extend('table', {
            allowAttributes: ['tableClasses', 'tableStyles', 'tableRole']
        });

        conversion.for('downcast')
            .attributeToAttribute({
                model: 'tableRole',
                view: 'role'
            });

        conversion.for('upcast')
            .attributeToAttribute({
                model: 'tableRole',
                view: 'role'
            });

        //upcast to handle styles and classes
        conversion.for('upcast').add(dispatcher =>
            dispatcher.on('element:table', (evt, data, conversionApi) => {
                if (!data.modelRange) {
                    return;
                }

                const modelElement = [...data.modelRange.getItems({ shallow: true })].pop();

                if (modelElement) {
                    //we dont want to handle styles that are already normalized (border and background stuff)
                    const stylesToSkip = Array.from(data.viewItem._styles._styleProcessor._normalizers.keys()).filter(registered => registered.startsWith('border') === true || registered.startsWith('background') === true);

                    //Also skip these settings because they have seperate input fields
                    stylesToSkip.push('width', 'height', 'float');

                    //create styles dictionary of the styles to consume
                    const stylesToConsume = data.viewItem._styles.getStyleNames().filter(style => stylesToSkip.indexOf(style) < 0);
                    let styles = {};
                    for (const styleToConsume of stylesToConsume) {
                        styles[styleToConsume] = data.viewItem.getStyle(styleToConsume);
                    }

                    if (Object.keys(styles).length > 0) {
                        const matcherPattern = {
                            _styles: styles
                        };

                        if (conversionApi.consumable.test(data.viewItem, matcherPattern)) {
                            conversionApi.consumable.consume(data.viewItem, matcherPattern);
                            conversionApi.writer.setAttribute('tableStyles', styles, modelElement);
                        }
                    }

                    const classesToConsume = data.viewItem._classes;
                    if (classesToConsume.size && classesToConsume.size > 0) {
                        const matcherPattern = {
                            _classes: classesToConsume
                        };

                        if (conversionApi.consumable.test(data.viewItem, matcherPattern)) {
                            conversionApi.consumable.consume(data.viewItem, matcherPattern);
                            conversionApi.writer.setAttribute('tableClasses', [...classesToConsume].filter(c => c !== ''), modelElement);
                        }
                    }
                }

            })
        );

        //downcast tableStyles
        conversion.for('downcast').add(dispatcher =>
            dispatcher.on(`attribute:tableStyles:table`, (evt, data, conversionApi) => {
                const { item, attributeNewValue, attributeOldValue } = data;
                const { mapper, writer } = conversionApi;

                if (!conversionApi.consumable.consume(data.item, evt.name)) {
                    return;
                }

                const table = [...mapper.toViewElement(item).getChildren()].find(child => child.is('element', 'table')) ?? mapper.toViewElement(item);
                if (table) {
                    if (attributeOldValue) {
                        for (const key of Object.keys(attributeOldValue)) {
                            writer.removeStyle(key, table);
                        }
                    }

                    if (attributeNewValue) {
                        for (const [key, value] of Object.entries(attributeNewValue)) {
                            writer.setStyle(key, value, table);
                        }
                    }
                }
            })
        );

        //downcast tableClasses
        conversion.for('downcast').add(dispatcher =>
            dispatcher.on(`attribute:tableClasses:table`, (evt, data, conversionApi) => {
                const { item, attributeNewValue, attributeOldValue } = data;
                const { mapper, writer } = conversionApi;

                if (!conversionApi.consumable.consume(data.item, evt.name)) {
                    return;
                }

                var table = [...mapper.toViewElement(item).getChildren()].find(child => child.is('element', 'table')) ?? mapper.toViewElement(item);
                if (table) {
                    if (attributeOldValue) {
                        attributeOldValue.forEach(classValue => {
                            writer.removeClass(classValue, table);
                        })
                    }

                    if (attributeNewValue && attributeNewValue.filter(c => c !== '').length > 0) {
                        attributeNewValue.forEach(classValue => {
                            writer.addClass(classValue, table);
                        })
                    }
                    else {
                        writer.removeAttribute('class', table);
                    }
                }
            })
        );

        //downcast to handle float
        this.editor.conversion.for('dataDowncast').add(dispatcher => {
            dispatcher.on('insert:table', (evt, data, conversionApi) => {
                const { item } = data;
                const { mapper, writer } = conversionApi;

                const table = mapper.toViewElement(item);

                if (table.getStyle('float') !== undefined) {
                    writer.setAttribute('align', table.getStyle('float'), table);
                }
                else {
                    writer.setAttribute('align', 'center', table);
                }
            });
        });

        //editing downcasts to center table in editor with margins
        this.editor.conversion.for('editingDowncast').add(dispatcher => {
            dispatcher.on('insert:table', (evt, data, conversionApi) => {
                const { item } = data;
                const { mapper, writer } = conversionApi;

                const table = mapper.toViewElement(item);

                if (table.getStyle('float') === undefined) {
                    writer.setStyle('margin-left', 'auto', table);
                    writer.setStyle('margin-right', 'auto', table);
                }
            });

            dispatcher.on('attribute:tableAlignment:table', (evt, data, conversionApi) => {
                const { item, attributeNewValue } = data;
                const { mapper, writer } = conversionApi;

                const table = mapper.toViewElement(item);

                if (attributeNewValue === null) { //center
                    writer.setStyle('margin-left', 'auto', table);
                    writer.setStyle('margin-right', 'auto', table);
                }

            });
        });

        this.listenTo(contextualBalloonPlugin, 'change:visibleView', (evt, name, visibleView) => {

            //if we opened the table properties view, create the labels and handle the styles/class attributes
            if (visibleView?.options?.defaultTableProperties) {
                const tablePropertiesView = visibleView;

                //Parse attributes values to string
                const table = this.getSelectedTableElement();
                if (table._attrs.get('tableStyles')) {
                    let stylesString = '';
                    for (const [key, value] of Object.entries(table._attrs.get('tableStyles'))) {
                        stylesString = stylesString + key + ':' + value + ';';
                    }

                    tablePropertiesView.tableStylesString = stylesString;
                }
                else {
                    tablePropertiesView.tableStylesString = '';
                }

                tablePropertiesView.tableClassString = table._attrs.get('tableClasses') ? Array.from(table._attrs.get('tableClasses')).join(' ') : '';


                //labels are already initialized --> just set the values accordingly
                if (tablePropertiesView.stylesField && tablePropertiesView.classesField) {
                    tablePropertiesView.classesField.fieldView.value = tablePropertiesView.tableClassString;
                    tablePropertiesView.stylesField.fieldView.value = tablePropertiesView.tableStylesString;
                }
                else {
                    const stylesField = this.createInput(tablePropertiesView, 'tableStylesString', t('Styles'));
                    const classesField = this.createInput(tablePropertiesView, 'tableClassString', t('Classes'));

                    //set local variables so we can check later if we inserted the labels yet
                    tablePropertiesView.stylesField = stylesField;
                    tablePropertiesView.classesField = classesField;

                    //On submit set the table attributes
                    tablePropertiesView.on('submit', (data) => {
                        const table = this.getSelectedTableElement();

                        if (table) {
                            let stylesDict = {};
                            let styles = data.path[0].tableStylesString.split(';');
                            styles.filter(style => style.indexOf(':') > -1).forEach(style => stylesDict[style.split(':')[0]] = style.split(':')[1]);

                            this.editor.model.change(writer => {
                                writer.setAttribute('tableClasses', data.path[0].tableClassString.split(' ').filter(c => c !== ''), table)
                                writer.setAttribute('tableStyles', stylesDict, table);
                            })
                        }
                    });
                }
            }
        });
    }

    getSelectedTableElement() {
        const selection = this.editor.model.document.selection;
        const selectedElement = selection.getSelectedElement();

        let table = null;
        if (selectedElement && selectedElement.is('element', 'table')) {
            table = selectedElement;
        }
        else {
            table = selection.getFirstPosition().findAncestor('table');
        }

        return table;
    }

    createInput(tablePropertiesView, viewPropertyName, label) {
        const inputField = new LabeledFieldView(this.locale, createLabeledInputText);
        inputField.set({
            label: label,
            class: 'ck ck-form__row'
        });
        inputField.render();
        tablePropertiesView.registerChild(inputField);
        tablePropertiesView.element.insertBefore(inputField.element, tablePropertiesView.children._items[tablePropertiesView.children._items.length - 1].element);

        //handle input binding
        inputField.fieldView.bind('value').to(tablePropertiesView, viewPropertyName);
        inputField.fieldView.on('input', () => {
            tablePropertiesView[viewPropertyName] = inputField.fieldView.element?.value;
        });

        return inputField;
    }
}